import {
  ButtonLink,
  Link,
  PlusIcon,
  Stack,
  Table,
  TableCell,
  TablePager,
  TableRow,
} from '@fukuri-admins/ui/common';
import { SearchForm } from './SearchForm';
import styles from './styles.module.scss';

/**
 * 企業契約期間テーブルの行データ型
 *
 * 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type ClientContractRow = {
  /** 企業契約期間 ID（React key 用） */
  id: number;
  /** 利用企業 ID（詳細画面 URL の :clientId 部分） */
  clientId: number;
  /** 利用企業名 */
  clientName: string;
  /** 契約プラン名 */
  contractPlanName: string;
  /** 契約プラン詳細画面への遷移パス */
  contractPlanLink: string;
  /** 開始日（表示整形済み：「YYYY/MM/DD」形式） */
  startedOn: string;
  /**
   * 終了日（表示整形済み：「YYYY/MM/DD」形式）
   *
   * 無期限契約の場合は「-」
   */
  endedOn: string;
  /** 作成日時（表示整形済み：「YYYY/MM/DD HH:mm」形式） */
  createdAt: string;
};

type Props = {
  /** テーブルに表示する企業契約期間行データ */
  clientContracts: ClientContractRow[];
  /** 利用企業名 Input の現在値 */
  clientNameValue: string;
  /** 契約プラン名 Input の現在値 */
  contractPlanNameValue: string;
  /** 利用企業名 Input 変更時のコールバック */
  onClientNameChange: (value: string) => void;
  /** 契約プラン名 Input 変更時のコールバック */
  onContractPlanNameChange: (value: string) => void;
  /** 検索ボタン押下時のコールバック */
  onSearch: () => void;
  /** 利用企業名 Input の × ボタン押下時のコールバック */
  onClientNameClear: () => void;
  /** 契約プラン名 Input の × ボタン押下時のコールバック */
  onContractPlanNameClear: () => void;
  /** ページネーション */
  pagerProps: React.ComponentProps<typeof TablePager>;
  /** 新規作成ボタンのリンク先 URL */
  createLink: string;
  /**
   * 行クリック時のコールバック（企業契約期間 詳細画面への遷移）
   *
   * @param termId - 企業契約期間 ID
   * @param clientId - 利用企業 ID
   */
  onRowClick: (termId: number, clientId: number) => void;
};

/** テーブルヘッダー定義（5 列） */
const TABLE_HEADERS: React.ComponentProps<typeof Table>['headers'] = [
  { type: 'text', label: '利用企業', className: styles.clientNameCol },
  { type: 'text', label: '契約プラン', className: styles.contractPlanCol },
  { type: 'text', label: '開始日', className: styles.colDate },
  { type: 'text', label: '終了日', className: styles.colDate },
  { type: 'text', label: '作成日時', className: styles.colDate },
];

/**
 * 企業契約期間一覧 Presentation
 *
 * - 5 列（利用企業 / 契約プラン / 開始日 / 終了日 / 作成日時）
 * - 行クリックで詳細画面（`/clients/:clientId/client-contracts/:termId`）に遷移する
 * - 検索 Input と Table は常時表示（0 件時は Table body が空になるだけ）
 * - リスト表示専用。Empty 表示の出し分けは Container 層で行う
 * - ヘッダー行: 「新規作成」ボタン（createLink へ遷移）
 */
export const ClientContractListPresentation: React.FC<Props> = ({
  clientContracts,
  clientNameValue,
  contractPlanNameValue,
  onClientNameChange,
  onContractPlanNameChange,
  onSearch,
  onClientNameClear,
  onContractPlanNameClear,
  pagerProps,
  createLink,
  onRowClick,
}) => (
  <Stack direction='column' gap={16}>
    <Stack direction='row' alignItems='flex-start' justifyContent='space-between'>
      <SearchForm
        clientNameValue={clientNameValue}
        contractPlanNameValue={contractPlanNameValue}
        onClientNameChange={onClientNameChange}
        onContractPlanNameChange={onContractPlanNameChange}
        onSearch={onSearch}
        onClientNameClear={onClientNameClear}
        onContractPlanNameClear={onContractPlanNameClear}
      />
      <ButtonLink
        to={createLink}
        variant='primary'
        size='md'
        icon={<PlusIcon size='xs' />}
        iconPosition='left'
      >
        新規作成
      </ButtonLink>
    </Stack>
    <Table headers={TABLE_HEADERS} scrollable>
      {clientContracts.map(row => (
        <TableRow key={row.id} onClick={() => onRowClick(row.id, row.clientId)}>
          <TableCell>{row.clientName}</TableCell>
          <TableCell>
            <Link
              to={row.contractPlanLink}
              isExternal
              target='_blank'
              onClick={e => e.stopPropagation()}
            >
              {row.contractPlanName}
            </Link>
          </TableCell>
          <TableCell>{row.startedOn}</TableCell>
          <TableCell>{row.endedOn}</TableCell>
          <TableCell>{row.createdAt}</TableCell>
        </TableRow>
      ))}
    </Table>
    <TablePager {...pagerProps} />
  </Stack>
);
